import { useEffect, useState } from 'react';
import { DownloadSimple, Tag } from 'shared/icons';
import api from '../../services/api';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Modal from '../ui/Modal';
import { MONO_CLASS, Mono } from '../ui/Mono';
import Select from '../ui/Select';
import { useToast } from '../ui/Toast';
import { ParseIntegerInput } from './locationFields';
import { useWarehouseLabels } from './warehouseLabels';
import { DownloadZplFile, SendZplToPrinter, useZplPrinters, ZplPrinterSelect } from './zplPrinting';

/**
 * Etiquetas em ZPL (GET /warehouse/locations/labels): o usuário escolhe o
 * ESCOPO — etiqueta de BLOCO (rack + bloco; a única etiqueta física do rack,
 * QR `A-01`) ou ENDEREÇO específico (código literal de staging/doca/
 * fábrica/incidente) — e o backend devolve `{zpl, preview}`. A UI mostra o
 * preview textual e oferece: [Enviar para impressora] (REDE cadastrada →
 * backend em POST /printers/:id/print; USB desta máquina → direto pelo QZ
 * Tray local) e [Baixar .zpl].
 */
const LocationLabelsModal = ({ isOpen, onClose, rackOptions = [], initialRack = '' }) => {
    const { label } = useWarehouseLabels();
    const toast = useToast();
    const [scope, setScope] = useState('block');
    // Rack é LETRA (prefixo opaco do porta-pallet); bloco é sequencial.
    const [rack, setRack] = useState('');
    const [bay, setBay] = useState('');
    const [code, setCode] = useState('');
    // M10: mídia da etiqueta. A 40×60 não é a 10×15 encolhida — é outro layout
    // (só QR + código), então a prévia muda junto.
    const [media, setMedia] = useState('100x150');
    // {zpl, preview} da última prévia gerada; some quando os inputs mudam.
    const [result, setResult] = useState(null);
    // M10: lote de etiquetas de bloco (as 48 do galpão de uma vez).
    const [batch, setBatch] = useState(null);
    const [formError, setFormError] = useState(null);
    const [loadingPreview, setLoadingPreview] = useState(false);
    const [sending, setSending] = useState(false);
    // Impressoras (rede cadastrada + USB desta máquina) — fluxo compartilhado
    // com a etiqueta de LPN do M3, em ./zplPrinting.
    const { printers, localPrinters, selectedPrinter, setSelectedPrinter } = useZplPrinters(isOpen);

    useEffect(() => {
        if (!isOpen) {
            return;
        }
        setScope('block');
        setRack(initialRack || (rackOptions.length > 0 ? String(rackOptions[0]) : ''));
        setBay('');
        setCode('');
        setMedia('100x150');
        setResult(null);
        setBatch(null);
        setFormError(null);
        // `rackOptions` fora das deps de propósito: o reset é do MOMENTO de
        // abrir; a lista chegando depois não deve limpar o que o usuário mexeu.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, initialRack]);

    const busy = loadingPreview || sending;

    const closeModal = () => {
        if (!busy) {
            onClose();
        }
    };

    // Qualquer mudança de input invalida a prévia (o que está na tela deve ser
    // exatamente o que seria impresso).
    const changeInput = (setter) => (value) => {
        setter(value);
        setResult(null);
        setBatch(null);
        setFormError(null);
    };
    const changeScope = changeInput(setScope);
    const changeRack = changeInput(setRack);
    const changeBay = changeInput(setBay);
    const changeCode = changeInput(setCode);
    const changeMedia = changeInput(setMedia);

    const handleGeneratePreview = async (event) => {
        event.preventDefault();
        setFormError(null);

        // M10: o LOTE tem caminho próprio — são N etiquetas, não uma.
        if (scope === 'rack') {
            setLoadingPreview(true);
            try {
                const data = await api.getWarehouseBlockLabelsBatch({
                    ...(rack ? { rack } : {}),
                    media,
                });
                setBatch(data?.labels?.length ? data : null);
                if (!data?.labels?.length) {
                    setFormError({ message: label('wms.labels.batch_empty') });
                }
            } catch (error) {
                setFormError({
                    message:
                        error?.code === 'NETWORK_ERROR'
                            ? label('wms.collector.error_network')
                            : error?.message || 'Erro ao gerar as etiquetas',
                });
            } finally {
                setLoadingPreview(false);
            }
            return;
        }

        const params = new URLSearchParams();
        params.set('media', media);
        if (scope === 'block') {
            if (!rack) {
                setFormError({ field: 'rack', message: label('wms.labels.rack_required') });
                return;
            }
            const bayNumber = ParseIntegerInput(bay);
            if (bayNumber === null || Number.isNaN(bayNumber) || bayNumber < 1) {
                setFormError({ field: 'bay', message: label('wms.labels.block_required') });
                return;
            }
            params.set('scope', 'block');
            params.set('rack', rack);
            params.set('bay', String(bayNumber));
        } else {
            const literal = code.trim();
            if (!literal) {
                setFormError({ field: 'code', message: label('wms.common.required') });
                return;
            }
            params.set('scope', 'address');
            params.set('code', literal);
        }

        setLoadingPreview(true);
        try {
            const data = await api.get(`/warehouse/locations/labels?${params.toString()}`);
            setResult(data?.zpl && data?.preview ? data : null);
            if (!data?.zpl) {
                setFormError({ message: 'Resposta inesperada do backend ao gerar a etiqueta.' });
            }
        } catch (error) {
            if (error?.code === 'NETWORK_ERROR') {
                setFormError({ message: label('wms.collector.error_network') });
            } else {
                setFormError({ message: error?.message || 'Erro ao gerar a etiqueta' });
            }
        } finally {
            setLoadingPreview(false);
        }
    };

    // Um lote é um fluxo ZPL com N formatos (`^XA…^XZ` repetidos) — é assim que
    // a impressora entende "imprima estas 48". Não existe um "modo lote": é a
    // concatenação, e ela vale igual para o envio e para o download.
    const zplToPrint = batch ? batch.labels.map((item) => item.zpl).join('\n') : result?.zpl;

    const handleSend = async () => {
        if (!zplToPrint || !selectedPrinter) {
            return;
        }
        setSending(true);
        try {
            const printerName = await SendZplToPrinter({
                target: selectedPrinter,
                printers,
                zpl: zplToPrint,
                label,
            });
            toast.success(`${label('wms.labels.sent')}${printerName ? ` — ${printerName}` : ''}`);
        } catch (error) {
            // Erros do envio precisam sair legíveis: rede/backend mandam a
            // mensagem da rota; USB manda a do QZ (não instalado, prompt de
            // permissão bloqueado…).
            if (error?.code === 'NETWORK_ERROR') {
                toast.error(label('wms.collector.error_network'));
            } else {
                toast.error(error);
            }
        } finally {
            setSending(false);
        }
    };

    const handleDownload = () => {
        if (!zplToPrint) {
            return;
        }
        if (batch) {
            DownloadZplFile(zplToPrint, `etiquetas-blocos-${batch.count}.zpl`);
            return;
        }
        const scanCode = result.preview?.scan_code || 'etiqueta';
        DownloadZplFile(zplToPrint, `etiqueta-${scanCode}.zpl`);
    };

    const scopeOptions = [
        { id: 'block', label: label('wms.labels.scope_block'), help: label('wms.labels.scope_block_help') },
        // M10: o galpão tem 48 blocos e a tela imprimia UM por vez.
        { id: 'rack', label: label('wms.labels.scope_street'), help: label('wms.labels.scope_street_help') },
        { id: 'address', label: label('wms.labels.scope_address'), help: label('wms.labels.scope_address_help') },
    ];
    const mediaOptions = [
        { id: '100x150', label: label('wms.labels.media_100x150') },
        { id: '40x60', label: label('wms.labels.media_40x60') },
        { id: '40x40', label: label('wms.labels.media_40x40') },
    ];
    const activeScope = scopeOptions.find((option) => option.id === scope);

    return (
        <Modal isOpen={isOpen} onClose={closeModal} title={label('wms.labels.zpl_title')} size="md">
            <form onSubmit={handleGeneratePreview} className="space-y-4">
                <p className="text-sm text-mono-600 dark:text-mono-400">{label('wms.labels.zpl_description')}</p>

                {/* Escopo da etiqueta (bloco × endereço específico) */}
                <div>
                    <div className="grid grid-cols-3 gap-2">
                        {scopeOptions.map((option) => (
                            <Button
                                key={option.id}
                                type="button"
                                variant={scope === option.id ? 'primary' : 'secondary'}
                                onClick={() => changeScope(option.id)}
                                disabled={busy}
                                aria-pressed={scope === option.id}
                            >
                                {option.label}
                            </Button>
                        ))}
                    </div>
                    {activeScope ? (
                        <p className="mt-2 text-xs text-mono-500 dark:text-mono-400">{activeScope.help}</p>
                    ) : null}
                </div>

                {/* M10: a MÍDIA muda o layout, não só o papel — a 40×60 sai só
                    com QR e código, sem o título gigante. */}
                <Select
                    label={label('wms.labels.media')}
                    value={media}
                    onChange={(event) => changeMedia(event.target.value)}
                    disabled={busy}
                    helperText={label('wms.labels.media_help')}
                >
                    {mediaOptions.map((option) => (
                        <option key={option.id} value={option.id}>
                            {option.label}
                        </option>
                    ))}
                </Select>

                {scope === 'rack' ? (
                    <Select
                        label={label('wms.field.rack')}
                        value={rack}
                        onChange={(event) => changeRack(event.target.value)}
                        disabled={busy}
                        helperText={label('wms.labels.scope_street_hint')}
                    >
                        <option value="">{label('wms.labels.all_streets')}</option>
                        {rackOptions.map((option) => (
                            <option key={option} value={String(option)}>
                                {`${label('wms.field.rack')} ${option}`}
                            </option>
                        ))}
                    </Select>
                ) : null}

                {scope === 'block' ? (
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <Select
                            label={label('wms.field.rack')}
                            value={rack}
                            onChange={(event) => changeRack(event.target.value)}
                            errorMessage={formError?.field === 'rack' ? formError.message : undefined}
                            disabled={busy || rackOptions.length === 0}
                        >
                            {rackOptions.length === 0 ? <option value="">{label('wms.labels.no_racks')}</option> : null}
                            {rackOptions.map((option) => (
                                <option key={option} value={String(option)}>
                                    {`${label('wms.field.rack')} ${option}`}
                                </option>
                            ))}
                        </Select>
                        <Input
                            label={label('wms.field.block')}
                            type="number"
                            min={1}
                            max={99}
                            value={bay}
                            onChange={(event) => changeBay(event.target.value)}
                            errorMessage={formError?.field === 'bay' ? formError.message : undefined}
                            disabled={busy}
                            autoComplete="off"
                            className={MONO_CLASS}
                        />
                    </div>
                ) : null}

                {scope === 'address' ? (
                    <Input
                        label={label('wms.field.code')}
                        value={code}
                        onChange={(event) => changeCode(event.target.value.toUpperCase())}
                        placeholder={label('wms.labels.code_placeholder')}
                        errorMessage={formError?.field === 'code' ? formError.message : undefined}
                        disabled={busy}
                        autoComplete="off"
                        className={MONO_CLASS}
                    />
                ) : null}

                {formError && !formError.field ? (
                    <p role="alert" className="text-sm text-danger-600 dark:text-danger-400">
                        {formError.message}
                    </p>
                ) : null}

                {/* Prévia textual — retrato do que sai impresso (title/lines/QR) */}
                {result ? (
                    <div className="space-y-2 rounded-lg border border-mono-200 bg-mono-50 p-4 dark:border-mono-800 dark:bg-mono-950">
                        <p className="text-xs font-medium uppercase tracking-wide text-mono-500 dark:text-mono-400">
                            {label('wms.labels.preview_title')}
                        </p>
                        <p className="text-2xl font-semibold text-black dark:text-white">{result.preview.title}</p>
                        {(result.preview.lines || []).map((line) => (
                            <Mono key={line} as="p" className="text-lg text-mono-700 dark:text-mono-300">
                                {line}
                            </Mono>
                        ))}
                        <p className="text-xs text-mono-500 dark:text-mono-400">
                            {label('wms.labels.scan_code')}: <Mono>{result.preview.scan_code}</Mono>
                        </p>
                    </div>
                ) : null}

                {/* M10: prévia do LOTE — quantas etiquetas e quais blocos. A
                    lista importa: é a conferência antes de gastar 48 etiquetas. */}
                {batch ? (
                    <div className="space-y-2 rounded-lg border border-mono-200 bg-mono-50 p-4 dark:border-mono-800 dark:bg-mono-950">
                        <p className="text-xs font-medium uppercase tracking-wide text-mono-500 dark:text-mono-400">
                            {label('wms.labels.batch_preview_title')}
                        </p>
                        <p className="text-2xl font-semibold text-black dark:text-white">
                            {label('wms.labels.batch_count', { count: batch.count })}
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                            {batch.labels.map((item, index) => (
                                // As 3 cópias da cabeceira têm o MESMO código —
                                // o índice entra na chave para não colidir.
                                <Mono
                                    key={`${item.preview.scan_code}-${index}`}
                                    className="rounded border border-mono-200 px-1.5 py-0.5 text-xs text-mono-700 dark:border-mono-700 dark:text-mono-300"
                                >
                                    {item.preview.scan_code}
                                </Mono>
                            ))}
                        </div>
                    </div>
                ) : null}

                {/* Ações da etiqueta gerada: enviar (rede cadastrada ou USB
                    desta máquina) ou baixar o .zpl */}
                {result || batch ? (
                    <div className="space-y-3">
                        <ZplPrinterSelect
                            label={label}
                            printers={printers}
                            localPrinters={localPrinters}
                            value={selectedPrinter}
                            onChange={setSelectedPrinter}
                            disabled={busy}
                        />
                        <div className="flex flex-col gap-3 sm:flex-row">
                            <Button
                                type="button"
                                onClick={handleSend}
                                loading={sending}
                                disabled={!selectedPrinter}
                                className="flex-1"
                            >
                                <Tag size={18} className="mr-2" />
                                {label('wms.labels.send')}
                            </Button>
                            <Button type="button" onClick={handleDownload} variant="secondary" disabled={sending}>
                                <DownloadSimple size={18} className="mr-2" />
                                {label('wms.labels.download')}
                            </Button>
                        </div>
                    </div>
                ) : null}

                <div className="flex flex-col gap-3 pt-2 sm:flex-row">
                    <Button type="submit" loading={loadingPreview} disabled={sending} className="flex-1">
                        {label('wms.labels.generate_preview')}
                    </Button>
                    <Button type="button" onClick={closeModal} variant="secondary" className="sm:w-auto">
                        {label('wms.common.close')}
                    </Button>
                </div>
            </form>
        </Modal>
    );
};

export default LocationLabelsModal;
